import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { User, Mail, Save, ArrowLeft, AlertCircle, CheckCircle, Eye, EyeOff, Trash2, XCircle } from 'lucide-react';

function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.name || '',
                email: user.email || '',
                role: user.role || ''
            }));
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        // Validate passwords match if password is being changed
        if (formData.password && formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        // Validate password length if password is being changed
        if (formData.password && formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            setLoading(false);
            return;
        }

        try {
            const payload = {
                name: formData.name,
                email: formData.email,
                role: formData.role
            };

            // Only include password if it's being changed
            if (formData.password) {
                payload.password = formData.password;
            }

            const response = await api.put(`/users/${user.id}`, payload);

            setSuccess('Profile updated successfully!');

            // Clear password fields
            setFormData(prev => ({
                ...prev,
                password: '',
                confirmPassword: ''
            }));

            // Update local user data
            localStorage.setItem('user', JSON.stringify(response.data));

        } catch (error) {
            setError(error.response?.data?.detail || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const openDeleteModal = () => {
        setShowDeleteModal(true);
        setDeleteError('');
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteError('');
    };

    const handleDeleteAccount = async () => {
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await api.delete(`/users/${user.id}`);
            logout();
            navigate('/login');
        } catch (error) {
            setDeleteError('Failed to delete account');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-6">
                <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium font-inter"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Dashboard
                </button>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="glass rounded-xl shadow-lg max-w-md w-full p-6 relative animate-fade-in border border-white/30">
                        <button
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                            onClick={closeDeleteModal}
                            aria-label="Close"
                        >
                            <XCircle className="h-6 w-6" />
                        </button>
                        <div className="flex flex-col items-center">
                            <Trash2 className="h-10 w-10 text-red-500 mb-2" />
                            <h2 className="text-lg font-semibold text-primary-900 mb-2 font-poppins">Delete Account?</h2>
                            <p className="text-gray-600 text-center mb-4 font-inter">Are you sure you want to delete your account? This action cannot be undone and all your feedback will be removed.</p>
                            {deleteError && (
                                <div className="bg-red-50 border border-red-200 rounded-md p-2 mb-2 w-full text-center text-red-600 text-sm font-inter">
                                    {deleteError}
                                </div>
                            )}
                            <div className="flex space-x-3 mt-2">
                                <button
                                    onClick={closeDeleteModal}
                                    className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium font-inter"
                                    disabled={deleteLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteAccount}
                                    className="px-4 py-2 rounded-md border border-transparent bg-red-600 text-white hover:bg-red-700 font-medium disabled:opacity-50 font-inter"
                                    disabled={deleteLoading}
                                >
                                    {deleteLoading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="glass shadow rounded-xl border border-white/30">
                <div className="px-6 py-4 border-b border-white/30">
                    <h2 className="text-lg font-semibold text-primary-900 font-poppins">Profile Settings</h2>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex items-center space-x-2 text-red-600 font-inter">
                                <AlertCircle className="h-4 w-4" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}

                    {success && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-4">
                            <div className="flex items-center space-x-2 text-green-600 font-inter">
                                <CheckCircle className="h-4 w-4" />
                                <span>{success}</span>
                            </div>
                        </div>
                    )}

                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-primary-700 mb-2 font-inter">
                            Full Name
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <User className="h-5 w-5 text-primary-400" />
                            </div>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                autoComplete="name"
                                className="block w-full pl-10 px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-inter"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-primary-700 mb-2 font-inter">
                            Email
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-primary-400" />
                            </div>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                required
                                autoComplete="email"
                                className="block w-full pl-10 px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-inter"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-primary-700 mb-2 font-inter">
                            Username
                        </label>
                        <input
                            type="text"
                            id="username"
                            value={user?.username || ''}
                            disabled
                            className="block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-50 text-gray-500 font-inter"
                        />
                        <p className="mt-1 text-sm text-primary-500 font-inter">Username cannot be changed</p>
                    </div>

                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-primary-700 mb-2 font-inter">
                            Role
                        </label>
                        <input
                            type="text"
                            id="role"
                            value={formData.role}
                            disabled
                            className="block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm bg-gray-50 text-gray-500 font-inter"
                            onChange={handleChange}
                        />
                        <p className="mt-1 text-sm text-primary-500 font-inter">Role cannot be changed</p>
                    </div>

                    <div className="border-t border-white/30 pt-6">
                        <h3 className="text-lg font-semibold text-primary-900 mb-4 font-poppins">Change Password</h3>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-primary-700 mb-2 font-inter">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        autoComplete="new-password"
                                        className="block w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-inter"
                                        placeholder="Leave blank to keep current password"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5 text-primary-400 hover:text-primary-600" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-primary-400 hover:text-primary-600" />
                                        )}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-primary-500 font-inter">
                                    At least 6 characters
                                </p>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary-700 mb-2 font-inter">
                                    Confirm New Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        autoComplete="new-password"
                                        className="block w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-inter"
                                        placeholder="Confirm new password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff className="h-5 w-5 text-primary-400 hover:text-primary-600" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-primary-400 hover:text-primary-600" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-inter"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 font-inter"
                        >
                            <Save className="h-4 w-4 mr-2" />
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>

                <div className="px-6 py-4 border-t border-white/30">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-semibold text-red-700 font-poppins">Danger Zone</h3>
                            <p className="text-sm text-red-500 font-inter">Permanently delete your account</p>
                        </div>
                        <button
                            onClick={openDeleteModal}
                            className="px-4 py-2 border border-red-400 rounded-lg shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 font-inter"
                        >
                            Delete Account
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Profile; 